import { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { t } from 'i18next';
import { Box, Button, Typography } from '@mui/material';
import { FEATURE_TAGS } from '../utils/FeatureTags';
import ApiManager from '../../libs/ApiManager';
import CircularIndeterminate from '../utils/Progress';
import Header, { VIEWS } from '../utils/Header';
import FormContext from '../context/FormContext';
import { parseText } from '../utils/utils';
import ErrorMessageContext from '../context/ErrorMessageContext';
import FAQs from '../components/FAQs';
import EventManager from '../../libs/EventManager';

import '../../styles/QuestionContainer.scss';

function findQuestion (questions, id) {
  const questionId = typeof id === 'number' ? id : parseInt(id);
  return questions.find(({ id }) => id === questionId);
}

function findAnswer (answers, id) {
  const answerId = typeof id === 'number' ? id : parseInt(id);
  return answers.find(({ id }) => id === answerId);
}

function mapHistory (history) {
  return history.map(({ id, name, value, answers }) => {
    const answer = findAnswer(answers, value) || {};
    return ({ id, question: parseText(name), value, answer: parseText(answer.title) });
  })
}

export default function QuestionContainer ({
  category,
  locale,
  onSignOut,
  onClose,
  onComplete
}) {
  const { form, onChange } = useContext(FormContext);
  const { onError }  = useContext(ErrorMessageContext);
  const [isLoading, setLoading] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [history, setHistory] = useState([]);
  const [faq, setFaq] = useState(null);
  const [faqArticles, setFaqArticles] = useState(null);
  const bodyRef = useRef();

  useEffect(() => {
    async function init () {
      const skipFaq = form.values && Object.keys(form.values).length > 0;
      let newHistory;
      setLoading(true);
      for (const { id, value } of form.history) {
        newHistory = await generateHistory(id, value, newHistory, skipFaq);
      }
      if (newHistory) {
        setHistory(newHistory);
      }
      setLoading(false);
    }
    if (Array.isArray(questions)) {
      if (Array.isArray(form?.history)) {
        init();
      } else {
        const question = questions.find(q => q.description === FEATURE_TAGS.question.first);
        let newHistory = null;
        if (question) {
          newHistory = [{
            id: question.id,
            name: question.name,
            answers: question.answers,
            value: null
          }];
        } else if (questions.length > 0) {
          newHistory = [{
            id: questions[0].id,
            name: questions[0].name,
            answers: questions[0].answers,
            value: null
          }];
        }
        if (newHistory) {
          onHistoryChange(newHistory);
        } else {
          onHistoryChange([]);
        }
      }
    }
  }, [form, questions]);

  useEffect(() => {
    async function fetchQuestions () {
      clearData();
      setLoading(true);
      try {
        const data = await ApiManager.listQuestions(category.id, locale);
        setQuestions(data);
      } catch (err) {
        onError(err);
      }
      setLoading(false);
    };
    if (category) {
      fetchQuestions();
    }
  }, [category, locale]);

  useEffect(() => {
    async function fetchArticles () {
      setLoading(true);
      try {
        const articles = await ApiManager.showArticles([...faq?.top, ...faq?.faqs].filter(Boolean), locale);
        setFaqArticles(articles);
      } catch (err) {
        onError(err);
      }
      setLoading(false);
    }
    if (faq?.top || faq?.faqs) {
      fetchArticles();
    }
  }, [faq, locale])

  async function handleChange (questionId, answerId) {
    const newHistory = await generateHistory(questionId, answerId);
    if (newHistory) {
      onHistoryChange(newHistory);
    }
  }

  async function generateHistory (questionId, answerId, prevHistory = history, skipFaq = false) {
    if (Array.isArray(questions)) {
      const question = findQuestion(questions, questionId);
      if (question && Array.isArray(question.answers)) {
        const answer = findAnswer(question.answers, answerId);
        const index = prevHistory.findIndex(({ id }) => id === questionId);
        let newHistory;
        if (index >= 0) {
          newHistory = prevHistory.slice(0, index);
        } else {
          newHistory = [...prevHistory];
        }
        if (answer) {
          newHistory.push({
            id: question.id,
            name: question.name,
            answers: question.answers,
            value: answerId,
          });
          if (answer.labels['next']) {
            const nextQuestion = findQuestion(questions, answer.labels['next'])
            newHistory.push({
              id: nextQuestion.id,
              name: nextQuestion.name,
              answers: nextQuestion.answers,
              value: null
            });
            setFaq(null);
            setFaqArticles(null);
            EventManager.clickSecondaryCategory(answer.id);
          } else if ((answer.labels['faq'] || answer.labels['top_faq'])) {
            setFaq({
              title: answer.title,
              body: answer.body,
              data: answer,
              top: answer.labels['top_faq'] || [],
              faqs: answer.labels['faq'] || [],
            });
            setFaqArticles(null);
            if (skipFaq) {
              onQuestionComplete(answer, newHistory, questions);
            }
            EventManager.clickSubcategory(answer.id, answer.labels['top_faq']);
          } else {
            setFaq(null);
            setFaqArticles(null);
            onQuestionComplete(answer, newHistory, questions);
            EventManager.clickSubcategory(answer.id);
            EventManager.clickYes(null);
          }
        } else {
          newHistory.push({
            id: question.id,
            name: question.name,
            answers: question.answers,
            value: null,
          })
        }
        return newHistory;
      }
    }
    return null;
  }

  function onHistoryChange (newHistory) {
    setHistory(newHistory);
    onChange({ history: mapHistory(newHistory) });
  }

  function onQuestionComplete (answer, newHistory) {
    onComplete(answer, mapHistory(newHistory), questions);
  }

  function onFaqSubmit () {
    onComplete(faq?.data, mapHistory(history), questions);
    EventManager.clickYes((Array.isArray(faq?.top) && faq.top.length > 0) ? Number(faq?.top[0]) : null);
  }

  function scrollTo (ref) {
    if (bodyRef.current && ref) {
      setTimeout(() => {
        ref.classList.add('fade');
      });
      setTimeout(() => {
        bodyRef.current.scrollTo({
          left: 0,
          top: ref.offsetTop,
          behavior: 'smooth'
        });
      }, 300);
    }
  }

  function onBack () {
    // handle form updated by onClose.
    clearData();
    onClose();
  }

  function clearData () {
    setHistory([]);
    setFaq(null);
    setFaqArticles(null);
  }

  return <Box className='question-container'>
    <Header title={t('header')} view={VIEWS.QUESTION} onSignOut={onSignOut} onBack={onBack} />
    <Box className='question-body'>
      <CircularIndeterminate show={isLoading} style={{ background: 'transparent' }} />
      <div className='questions' ref={bodyRef}>
        {
          history.map((item, index) => <Box
            key={`${item.id}-${index}`}
            className={classnames('question', { 'fade': item.value })}
            ref={!item.value ? scrollTo : undefined}
          >
            {index === 0 && <Typography className='main-title'>{category ? category.name : ''}</Typography>}
            <Typography className='title'>{parseText(item.name)}</Typography>
            {
              item.answers.map(answer => <Button
                key={answer.id}
                className={classnames('answer', { 'selected' : item.value === answer.id })}
                color='normal'
                onClick={() => handleChange(item.id, answer.id)}
                fullWidth
              >{parseText(answer.title)}</Button>)
            }
          </Box>)
        }
        <FAQs
          show={Array.isArray(faqArticles)}
          topFaq={(Array.isArray(faq?.top) && faq.top.length > 0) ? Number(faq?.top[0]) : null}
          faq={faq?.data}
          articles={faqArticles}
          onSubmit={onFaqSubmit}
          innerRef={scrollTo}
        />
      </div>
    </Box>
  </Box>
}